<i18n
  src="@/locales/views/service_provider/tech_visit/producer_inspectors/list_producers.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/producer.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/crop_field.yml" lang="yaml"></i18n>

<template>
  <bread-crumbs :labels="[module_name]" />
  <template v-if="load_data_general">
    <tabs-container v-model="option_tabs" :with_routing="false" />

    <template v-if="current_tab_data_charged">
      <div class="mt-8 flex flex-row justify-between">
        <div class="panel-plain-label space-x-2">
          <badge-default
            :label="
              total_producers[current_tab?.id] +
              ' ' +
              $t('labels.total_number_producers')
            "
          />
          <badge-default
            :label="
              total_hectares[current_tab?.id] +
              ' ' +
              $t('labels.total_hectares')
            "
          />
        </div>
        <div>
          <new-button
            @click.prevent="goToNew"
            :label="$t('labels.new_button')"
          />
        </div>
      </div>

      <div class="mt-8 flex flex-col">
        <ag-grid-vue
          class="ag-theme-alpine"
          :columnDefs="columnDefs"
          :defaultColDef="defaultColDef"
          :rowData="current_producers"
          domLayout="autoHeight"
        >
        </ag-grid-vue>
      </div>

      <table-paginator
        v-if="total_producers[current_tab?.id] > rows_per_page"
        v-model="current_page[current_tab?.id]"
        :init_current_page="current_page[current_page?.id]"
        :total_rows="total_producers[current_tab?.id]"
        :rows_per_page="rows_per_page"
      />
    </template>

    <loading-message v-else />
  </template>
  <loading-message v-else />
</template>

<script>
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue3";

import Requester from "@/views/service_provider/requesters/tech_visits/producer_inspectors/list_producers.js";
import ManageRequester from "@/views/service_provider/requesters/tech_visits/producer_inspectors/manage_producer_inspectors.js";

import TablePaginator from "@/components/views/layout/TablePaginator.vue";
import DestroyButton from "@/components/views/layout/ag_grid/DestroyButton.vue";

export default {
  name: "SPTechVisitsProducerInspectorsListProducers",
  components: { AgGridVue, TablePaginator },
  data() {
    return {
      load_data_general: false,
      downloading_crop_fields: false,
      current_page: {},
      rows_per_page: 10,
      inspectors: [],
      option_tabs: [],
      total_producers: {},
      total_hectares: {},
      paginated_producers: {},
      defaultColDef: {
        sortable: true,
        filter: true,
        floatingFilter: false,
      },
      columnDefs: [
        {
          headerName: this.$t(
            "model.producer.organization_producer.producer_code"
          ),
          field: "organization_producer.producer_code",
          filter: true,
        },
        {
          headerName: this.$t("model.producer.producer_code"),
          field: "producer_code",
          filter: true,
        },
        {
          headerName: this.$t("model.producer.lastname"),
          field: "lastname",
          filter: true,
          width: 300,
        },
        {
          headerName: this.$t("model.producer.name"),
          field: "name",
          filter: true,
          width: 300,
        },
        {
          headerName: this.$t("model.producer.producer_crops"),
          filter: true,
          valueGetter: (params) => {
            return params.data.producer_crops
              .map((pc) => pc.crop.name)
              .join(", ");
          },
        },
        {
          headerName: this.$t(
            "model.producer.organization_crop_fields.crop_field_code"
          ),
          filter: true,
          valueGetter: (params) => {
            return params.data.organization_crop_field_codes.join(", ");
          },
        },
        {
          headerName: this.$t("model.crop_field.hectares"),
          field: "hectares",
          filter: "agNumberColumnFilter",
          valueFormatter: (params) => {
            return this.numberWithPrecision(params.value);
          },
        },
        {
          headerName: "",
          cellRendererSelector: (params) => {
            return {
              component: DestroyButton,
              params: {
                destroy_label: "",
                destroy_title: this.$t("labels.destroy_item_button_title"),
                destroy_message: this.$t("labels.destroy_item_button_message", {
                  producer: `${params.data.lastname}, ${params.data.name}`,
                  inspector: this.current_tab.name,
                }),
                destroy_label_confirm: this.$t(
                  "labels.destroy_item_button_label_confirm"
                ),
                destroyEvent: this.destroy,
              },
            };
          },
        },
      ],
    };
  },
  props: {
    service_provider_id: {
      required: true,
    },
  },
  methods: {
    fetchData: async function () {
      await this.fetchInspectors();
      this.fillOptions();
      this.load_data_general = true;
    },
    fetchInspectors: async function () {
      let { data } = await Requester.fetchInspectors(this.service_provider_id);
      data.forEach((inspector) => (this.current_page[inspector.id] = 1));
      this.inspectors = data;
    },
    fetchProducers: async function () {
      const inspector_id = this.current_tab?.id;
      if (!inspector_id) return;
      this.paginated_producers[inspector_id] = null;

      let response = await Requester.fetchProducers(
        this.service_provider_id,
        inspector_id,
        {
          current_page: this.current_page[inspector_id],
          rows_per_page: this.rows_per_page,
        }
      );

      this.total_producers[inspector_id] = response.data.total;
      this.total_hectares[inspector_id] = this.numberWithPrecision(
        response.data.total_area ?? 0
      );
      this.paginated_producers[inspector_id] = response.data.records;
    },

    destroy: async function (producer) {
      try {
        const index =
          this.paginated_producers[this.current_tab?.id].indexOf(producer);
        let response = await ManageRequester.destroy(
          this.service_provider_id,
          this.current_tab?.id,
          producer.id
        );
        this.paginated_producers[this.current_tab?.id].splice(index, 1);
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      }
    },

    fillOptions: function () {
      this.option_tabs = this.inspectors.map((location) => {
        return {
          id: location.id,
          name: location.name,
          icon: "fa-solid fa-user",
        };
      });
      if (this.option_tabs.length == 0) return;
      this.option_tabs[0].current = true;
    },
    goToNew: function () {
      this.$router.push({
        name: this.routeName("new"),
        params: {
          service_provider_id: this.service_provider_id,
          inspector_id: this.current_tab.id,
        },
      });
    },
  },
  watch: {
    current_tab: function () {
      this.fetchProducers();
    },
    current_page: {
      handler: async function () {
        await this.fetchProducers();
      },
      deep: true,
    },
  },
  computed: {
    current_tab: function () {
      let current_tab = null;
      const options = this.option_tabs;
      Object.keys(options).forEach((key) => {
        if (options[key].current) return (current_tab = options[key]);
      });
      return current_tab;
    },
    current_producers: function () {
      return this.paginated_producers[this.current_tab?.id] || [];
    },
    current_tab_data_charged: function () {
      return this.paginated_producers[this.current_tab?.id];
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          service_provider_id: this.service_provider_id,
        },
      }).then(() => this.fetchData())
    );
  },
};
</script>
